import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal } from "../Reveal";
import { PHASES, exactEvent, illumination, moonAge, moonPath, phaseKey } from "../../lib/moon";

const DOW = ["L", "M", "X", "J", "V", "S", "D"];
const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

export const Moon = ({ age, size = 24 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
    <circle cx="12" cy="12" r="10" fill="#0B2216" stroke="rgba(246,238,220,0.25)" />
    <path d={moonPath(age)} fill="#F6EEDC" />
  </svg>
);

const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export const JLunar = () => {
  const today = new Date();
  const [view, setView] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [sel, setSel] = useState(today);

  const days = useMemo(() => {
    const y = view.getFullYear();
    const m = view.getMonth();
    const lead = (new Date(y, m, 1).getDay() + 6) % 7;
    const n = new Date(y, m + 1, 0).getDate();
    return [...Array(lead).fill(null), ...Array.from({ length: n }, (_, i) => new Date(y, m, i + 1))];
  }, [view]);

  const move = (d) => setView((v) => new Date(v.getFullYear(), v.getMonth() + d, 1));
  const selAge = moonAge(sel);
  const selPhase = PHASES[phaseKey(selAge)];
  const selEvent = exactEvent(sel);

  return (
    <section id="calendario-lunar" data-testid="lunar-calendar-section" className="relative py-24 md:py-36 bg-[#0B2216] overflow-hidden">
      <div className="absolute -right-32 -top-32 w-[520px] h-[520px] rounded-full bg-[radial-gradient(circle,rgba(246,238,220,0.12),transparent_65%)] pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal className="mb-12 md:mb-16 grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-7">
            <p className="font-mono-d text-[11px] tracking-[0.22em] uppercase text-[#8CD05A]">Calendario lunar Jungle</p>
            <h2 className="font-serif-d font-black text-5xl md:text-7xl leading-[0.92] tracking-tight mt-6">
              Cultiva <span className="italic font-semibold text-[#F2B441]">con la luna.</span>
            </h2>
          </div>
          <p className="md:col-span-4 md:col-start-9 text-[#C9D6C2] leading-relaxed">Fase lunar de cada día y las tareas que la tradición de cultivo recomienda en cada una. Toca un día para ver qué hacer.</p>
        </Reveal>

        <div className="grid lg:grid-cols-12 gap-6">
          <Reveal className="lg:col-span-8">
            <div data-testid="lunar-calendar" className="rounded-[10px] label-frame bg-[#143823] p-4 md:p-6">
              <div className="flex items-center justify-between mb-5">
                <button data-testid="lunar-prev-month" onClick={() => move(-1)} aria-label="Mes anterior" className="w-10 h-10 rounded-full border border-[#F6EEDC]/30 grid place-items-center hover:border-[#F2B441] hover:text-[#F2B441] transition-colors"><ChevronLeft className="w-4 h-4" /></button>
                <p data-testid="lunar-month-label" className="font-label text-2xl md:text-3xl text-label-gold">{MONTHS[view.getMonth()]} {view.getFullYear()}</p>
                <button data-testid="lunar-next-month" onClick={() => move(1)} aria-label="Mes siguiente" className="w-10 h-10 rounded-full border border-[#F6EEDC]/30 grid place-items-center hover:border-[#F2B441] hover:text-[#F2B441] transition-colors"><ChevronRight className="w-4 h-4" /></button>
              </div>
              <div className="grid grid-cols-7 gap-1 md:gap-2">
                {DOW.map((d) => <p key={d} className="text-center font-mono-d text-[11px] text-[#C9D6C2] pb-2">{d}</p>)}
                {days.map((d, i) => {
                  if (!d) return <span key={`e${i}`} />;
                  const age = moonAge(d);
                  const ph = PHASES[phaseKey(age)];
                  const ev = exactEvent(d);
                  const isSel = sameDay(d, sel);
                  return (
                    <motion.button key={d.getDate()} whileTap={{ scale: 0.94 }} onClick={() => setSel(d)} data-testid={`lunar-day-${d.getDate()}`}
                      className={`relative flex flex-col items-center gap-1 rounded-[6px] py-2 md:py-3 border transition-colors ${isSel ? "border-[#F2B441] bg-[#F2B441]/10" : "border-transparent hover:border-[#F6EEDC]/20"}`}>
                      <span className={`text-xs md:text-sm font-mono-d ${sameDay(d, today) ? "text-[#F2B441] font-bold" : "text-[#F6EEDC]"}`}>{d.getDate()}</span>
                      <Moon age={age} size={22} />
                      <span className="h-1 w-5 rounded-full" style={{ background: ph.tone }} />
                      {ev && <span className="hidden md:block absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-[#F2B441]" title={ev} />}
                    </motion.button>
                  );
                })}
              </div>
              <div className="flex flex-wrap gap-4 mt-5 font-mono-d text-[11px] text-[#C9D6C2]">
                {Object.values(PHASES).map((p) => <span key={p.name} className="flex items-center gap-2"><i className="w-4 h-1 rounded-full" style={{ background: p.tone }} />{p.name}</span>)}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-4">
            <div data-testid="lunar-day-detail" className="h-full rounded-[10px] bg-blue-leaves border border-[#F6EEDC]/25 p-6 md:p-8 flex flex-col">
              <p className="font-mono-d text-[11px] tracking-[0.22em] uppercase text-[#F2B441]">
                {sel.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })}
              </p>
              <div className="flex items-center gap-5 mt-6">
                <motion.div key={sel.toDateString()} initial={{ rotate: -30, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} transition={{ duration: 0.6 }}>
                  <Moon age={selAge} size={84} />
                </motion.div>
                <div>
                  <p data-testid="lunar-phase-name" className="font-serif-d font-black text-3xl leading-none">{selEvent || selPhase.name}</p>
                  <p className="font-mono-d text-xs text-[#E3EADF] mt-2">Iluminación {Math.round(illumination(selAge) * 100)} % · día {Math.floor(selAge) + 1} del ciclo</p>
                </div>
              </div>
              <p className="font-mono-d text-[11px] tracking-[0.22em] uppercase text-[#F2B441] mt-8 mb-3">Tareas recomendadas</p>
              <ul className="space-y-3">
                {selPhase.tasks.map((t) => (
                  <li key={t} className="flex gap-3 text-sm text-[#F6EEDC]"><span className="mt-1.5 w-2 h-2 rounded-full shrink-0" style={{ background: selPhase.tone }} />{t}</li>
                ))}
              </ul>
              <p className="text-xs text-[#E3EADF]/90 mt-auto pt-8">Recomendaciones basadas en la tradición del cultivo lunar. Prioriza siempre el estado real de tu planta.</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
